/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/* ===== Acordion ===== */
(() => {
  const initAccordionItems = (root = document) => {
    root.querySelectorAll(".c-accordion__item").forEach((item) => {
      if (item.dataset.accordionInitialized === "true") return;

      const content = item.querySelector(".c-accordion__content");
      if (!content) return;

      content.style.height = item.classList.contains("is-open") ? "auto" : "0px";
      item.dataset.accordionInitialized = "true";
    });
  };

  const toggleAccordionItem = (item) => {
    const content = item.querySelector(".c-accordion__content");
    if (!content) return;

    const isOpen = item.classList.contains("is-open");

    if (isOpen) {
      if (content.style.height === "auto") {
        content.style.height = content.scrollHeight + "px";
      }
      requestAnimationFrame(() => {
        content.style.height = "0px";
      });
      item.classList.remove("is-open");
      return;
    }

    content.style.height = content.scrollHeight + "px";
    item.classList.add("is-open");

    const onTransitionEnd = () => {
      if (item.classList.contains("is-open")) {
        content.style.height = "auto";
      }
      content.removeEventListener("transitionend", onTransitionEnd);
    };
    content.addEventListener("transitionend", onTransitionEnd);
  };

  document.addEventListener("DOMContentLoaded", () => {
    initAccordionItems();

    document.body.addEventListener("click", (event) => {
      const toggle = event.target.closest(".c-accordion__toggle");
      if (!toggle) return;

      const item = toggle.closest(".c-accordion__item");
      if (!item) return;

      initAccordionItems(item.closest("body") || document);
      toggleAccordionItem(item);
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(".c-accordion__item")) {
            initAccordionItems(node.parentElement || node);
          } else if (node.querySelector(".c-accordion__item")) {
            initAccordionItems(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
})();


/* ===== Layout Filter ===== */
document.addEventListener("DOMContentLoaded", function () {
  document.body.addEventListener("click", function (e) {
    const button = e.target.closest(".c-filter-layout__button");
    if (!button) return;

    const productGrid = document.querySelector(".product-facet__product-list");
    if (!productGrid) return;

    const layoutButtons = button.closest(".c-filter-layout")?.querySelectorAll(".c-filter-layout__button");
    if (layoutButtons) {
      layoutButtons.forEach(btn => btn.classList.remove("is-active"));
    }

    button.classList.add("is-active");

    productGrid.classList.remove(
      "product-facet__product-list--col2-mobile",
      "product-facet__product-list--col1-mobile"
    );

    const layout = button.dataset.layout;
    if (layout === "2") {
      productGrid.classList.add("product-facet__product-list--col2-mobile");
    } else if (layout === "1") {
      productGrid.classList.add("product-facet__product-list--col1-mobile");
    }
  });
});


/* ===== Modal ===== */
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const modalOpenButtons = document.querySelectorAll(".jsModalOpen");
    const modalWishlistLoginButtons = document.querySelectorAll(".jsWishlistButtonLogin");
    const modalCloseButtons = document.querySelectorAll(".jsModalClose");
    const modalPopups = document.querySelectorAll(".c-modal-popup");
    const modalWishlistLogin = document.getElementById("jsWishlistModal-Login");

    // 共通関数
    const openModal = (popup) => {
      if (!popup) return;
      popup.setAttribute("open", "true");
      document.documentElement.classList.add("lock-all");
    };

    const closeModal = (popup) => {
      if (!popup) return;
      popup.setAttribute("open", "false");
      document.documentElement.classList.remove("lock-all");
    };

    // 通常モーダルオープン
    // 商品カード内に置かれたモーダルは、アプリ（Stamp）のCSS isolation:isolate で
    // カード内に閉じ込められ隣のカードの下に潜るため、開くときに body 直下へ移す
    modalOpenButtons.forEach((button) => {
      const popup = button.nextElementSibling;
      button.addEventListener("click", () => {
        if (popup && popup.parentElement !== document.body) {
          document.body.appendChild(popup);
          popup.offsetWidth; // 移動直後に一度描画させ、開くアニメーションを効かせる
        }
        openModal(popup);
      });
    });

    // お気に入りログインモーダルオープン
    modalWishlistLoginButtons.forEach((button) => {
      button.addEventListener("click", () => {
        openModal(modalWishlistLogin);
      });
    });

    // オーバーレイ or クローズ要素クリックで閉じる
    modalPopups.forEach((popup) => {
      popup.addEventListener("click", (event) => {
        if (
          event.target.classList.contains("c-modal-popup__overlay") ||
          event.target.classList.contains("jsModalClose")
        ) {
          closeModal(popup);
        }
      });
    });

    // クローズボタン専用処理
    modalCloseButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const popup = button.closest(".c-modal-popup");
        closeModal(popup);
      });
    });
  });
})();

/*
 * 完売商品の価格非表示: 商品カードの Sold out 表記の保護
 *
 * 商品一覧では Stamp（Variants on Collection）アプリが商品を色ごとのカードに分割し、
 * カード内の価格要素を色の価格で書き換える。snippets/product-item.liquid は対象商品の
 * 文言を price クラスなしで出力して書き換えを避けているが、Stamp の更新で価格要素の
 * 探し方が変わると、エラーもなく価格が再表示される状態に戻ってしまう。
 * そのため data-price-soldout-label を持つ価格欄に価格が入ったら文言に戻し、
 * 気づけるようにコンソールへ警告を出す（2026-09-29 Issue #25）。
 */
(function () {
  var SELECTOR = '.price-list[data-price-soldout-label]';

  function restore(priceList) {
    var label = priceList.getAttribute('data-price-soldout-label');
    var current = priceList.querySelector('.price-soldout-label');

    if (current && priceList.children.length === 1 && current.textContent.trim() === label) {
      return;
    }

    priceList.innerHTML = '';
    var span = document.createElement('span');
    span.className = 'price-soldout-label';
    span.textContent = label;
    priceList.appendChild(span);

    var card = priceList.closest('product-item');
    var link = card ? card.querySelector('a[href*="/products/"]') : null;
    console.warn('[price-soldout] 商品カードの Sold out 表記が書き換えられたため元に戻しました。Stamp アプリの仕様変更を確認してください。', link ? link.getAttribute('href') : '');
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTOR)) restore(root);
    root.querySelectorAll(SELECTOR).forEach(restore);
  }

  function init() {
    scan(document);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        var priceList = target ? target.closest(SELECTOR) : null;

        if (priceList) {
          restore(priceList);
        }

        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) scan(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
